/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import { BEM } from "@adapptio/lib-components";
import { AssetEdit } from "../AssetEdit";
import { useResourceInfo } from "../../../../ui/hooks/useResourceInfo";
import { ToolbarSpacer } from "../../../../ui/components/Toolbar/ToolbarSpacer";
import { CMD } from "../../../../commands/CMD";
import { ToolbarCommandButton } from "../../../../ui/components/Toolbar/ToolbarCommandButton";
import { ABILITY_ACTIONS } from "../../../../ability/AbilityService";
import { FC_ANCHOR } from "../../../../ui/components/FloatingContainer";
import { DROPDOWN_STYLE } from "../../../../ui/components/DropdownMenu";
import { useAbility } from "../../../../ui/hooks";

export interface IAssetToolbarProps {
	className?: string;
	view: AssetEdit;
}

export const AssetToolbar: React.FC<IAssetToolbarProps> = ({ className, view }) => {
	const baseClass = BEM("edt-asset-toolbar");
	const ability = useAbility();

	const resourceInfo = useResourceInfo(view);

	if (className) {
		baseClass.add(className);
	}

	return (
		<>
			<ToolbarSpacer grow={1} />

			<ToolbarCommandButton
				icon="mdi/upload"
				label="Update asset"
				commandId={CMD.ASSET.EDIT.UPDATE}
				commandArgs={[ resourceInfo?.resource?.id ]}
			/>

			<ToolbarSpacer />

			<ToolbarCommandButton
				icon="mdi/menu"
				tooltip="More options"
				menuItems={[
					{
						id: "rename",
						label: "Rename",
						icon: "mdi/pencil",
						commandId: "resource.rename",
						commandArgs: [ resourceInfo.resource?.id ],
						disabled: !ability.can(ABILITY_ACTIONS.RENAME, resourceInfo.resource)
					},
					{
						id: "move",
						label: "Move",
						icon: "mdi/folder-move",
						commandId: "resource.moveDialog.open",
						commandArgs: [ resourceInfo.resource?.id ],
						disabled: !ability.can(ABILITY_ACTIONS.RENAME, resourceInfo.resource)
					},
					{
						id: "duplicate",
						label: "Duplicate",
						icon: "mdi/content-copy",
						commandId: "resource.duplicate",
						commandArgs: [ resourceInfo.resource?.id, true ]
					},
					{
						id: "history",
						label: "Revision History",
						icon: "mdi/history",
						commandId: "resource.openRevisionHistory",
						commandArgs: [ resourceInfo.resource?.id ],
						disabled: true
					},
					{
						id: "export",
						label: "Export to YAML",
						icon: "mdi/export",
						commandId: "resource.export",
						commandArgs: [ resourceInfo.resource?.id ]
					},
					{
						id: "delete",
						label: "Delete",
						icon: "mdi/delete",
						commandId: "resource.delete",
						commandArgs: [ resourceInfo.resource?.id ],
						disabled: !ability.can(ABILITY_ACTIONS.REMOVE, resourceInfo.resource)
					}
				]}
				menuAnchor={FC_ANCHOR.RIGHT_BOTTOM}
				menuStyle={DROPDOWN_STYLE.COMPACT}
			/>
		</>
	);
};
